import React, { useState, useEffect } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  ReferenceInput,
  AutocompleteInput,
  DateInput,
  NumberInput,
  required,
  useDataProvider,
  useNotify,
  useRedirect,
  useRefresh,
  FormDataConsumer,
} from 'react-admin';
import { FormToolbar } from '../../common/FormToolbar';
import { Card, CardContent, Typography, Box, Button, Grid, TextField as MuiTextField, Paper } from '@material-ui/core';

export const SalesPackingCreate = (props: any) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const [orderDetails, setOrderDetails] = useState<any>(null);
  // Track the design code of the selected order so bill numbers can be generated per design
  const [orderDesignCode, setOrderDesignCode] = useState<string | undefined>();
  // Cache product group & customer details for view-only cards
  const [productGroup, setProductGroup] = useState<any>(null);
  const [customer, setCustomer] = useState<any>(null);

  const padNumber = (n: number, length: number = 5): string => {
    return n.toString().padStart(length, '0');
  };

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderDetails) return;

      try {
        // Get order ID from the selected value
        const orderId = typeof orderDetails === 'object' ? orderDetails.id : orderDetails;
        
        console.log('Fetching order details for ID:', orderId);
        
        // Fetch order details
        const orderResponse = await dataProvider.getOne('orders', { id: orderId });
        const order = orderResponse.data;
        
        console.log('Order data:', order);

        // Fetch customer details
        const customerResponse = await dataProvider.getOne('customers', { id: order.customerId });
        const customerRec = customerResponse.data;
        setCustomer(customerRec);
        
        console.log('Customer data:', customerRec);

        // Fetch product group for carton capacity
        let cartonCapacity = 200; // default
        if (order.designCode) {
          try {
            // In orders, designCode stores the product-group id; load that record directly
            const pgResponse = await dataProvider.getOne('product-groups', { id: order.designCode });
            const pg = pgResponse.data;
            console.log('Product group record:', pg);
            cartonCapacity = pg.cartonCapacity || cartonCapacity;
            setProductGroup(pg);
          } catch (e) {
            console.log('Product group fetch failed:', e);
            // Use default capacity if product group fetch fails
          }
        }

        // Track design code for bill number generation
        setOrderDesignCode(order.designCode);

        // Update form values
        const form = props.form;
        if (form) {
          form.change('designCode', order.designCode);
          form.change('customerId', order.customerId);
          form.change('cartonCapacity', cartonCapacity);
          form.change('orderId', order.id);
          
        }

      } catch (error) {
        console.error('Error fetching order details:', error);
        notify(`Error fetching order details: ${error}`, 'error');
      }
    };

    fetchOrderDetails();
  }, [orderDetails, dataProvider, notify, props.form]);

  const handleSubmit = async (data: any) => {
    try {
      // Generate bill number if not provided, pattern: F01/00001
      let billNo: string | undefined = data.billNo;
      if (!billNo) {
        try {
          // Determine prefix from selected order's design code, e.g. F01, F02
          const prefix = (orderDesignCode || '').toString() || 'F01';

          const existing: any = await dataProvider.getList('sales-bills', {
            pagination: { page: 1, perPage: 1 },
            sort: { field: 'billNo', order: 'DESC' },
            filter: { billNo: { like: `${prefix}/%` } },
          });

          let nextSeq = 1;
          if (existing.data && existing.data.length > 0) {
            const last = existing.data[0].billNo as string;
            const parts = (last || '').split('/');
            const lastNum = parseInt(parts[1] || '0', 10);
            if (!isNaN(lastNum)) {
              nextSeq = lastNum + 1;
            }
          }
          const seqStr = nextSeq.toString().padStart(5, '0');
          billNo = `${prefix}/${seqStr}`;
        } catch (e) {
          // Fallback if sequence fetch fails
          billNo = `${orderDesignCode || 'F01'}/00001`;
        }
      }

      // Create sales bill using existing endpoint
      const created: any = await dataProvider.create('sales-bills', {
        data: {
          billNo: billNo,
          billDate: data.billDate ? new Date(data.billDate).toISOString() : new Date().toISOString(),
          designCode: data.designCode,
          orderId: data.orderId,
          customerId: data.customerId,
          createdDate: new Date().toISOString(),
        },
      });

      notify('Sales packing created successfully', 'success');
      // Immediately go to Edit screen for this bill so user can scan items there
      const newId = created?.data?.id;
      const basePath = (props && (props.basePath || props.resource && `/${props.resource}`)) || '/sales-bills';
      if (newId != null) {
        redirect('edit', basePath, newId, created.data);
      } else {
        redirect('list', basePath);
      }
    } catch (error) {
      notify((error as any)?.message || 'Error creating sales packing', 'error');
    }
  };

  return (
    <Create {...props} title="Create Sales Packing">
      <SimpleForm
        toolbar={<FormToolbar onSave={handleSubmit} onClose={props.onClose} onCancel={props.onCancel} {...props} />}
        variant="outlined"
        save={handleSubmit}
      >
        <TextInput source="billNo" label="Sales Bill No" fullWidth disabled />
        <DateInput source="billDate" label="Bill Date" fullWidth defaultValue={new Date().toISOString()} />
        
        <ReferenceInput
          source="orderId"
          reference="orders"
          label="Sales Order No"
          fullWidth
          filterToQuery={searchText => ({ 'orderNo~like': searchText })}
          perPage={100}
          onChange={(value: any) => {
            
            if (value) {
              setOrderDetails(value);
            }
          }}
        >
          <AutocompleteInput
            optionText="orderNo"
            helperText="Enter Sales Order Number"
            matchSuggestion={(filterValue, option) => {
              if (!option || !filterValue) return true;
              const rec = (option as any).record || option;
              const orderNo = (rec.orderNo || '').toLowerCase();
              const filter = filterValue.toLowerCase();
              return orderNo.includes(filter);
            }}
          />
        </ReferenceInput>

        <FormDataConsumer>
          {({ formData }) => {
            if (!formData.orderId) return null;

            return (
              <>
                {/* Design + image + carton capacity (view only) */}
                <Box mt={2} mb={1}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1">Design &amp; Carton Capacity</Typography>
                      {productGroup && (
                        <Box display="flex" alignItems="center" mt={1}>
                          {productGroup.photograph && (
                            <Box mr={2}>
                              <img
                                src={productGroup.photograph}
                                alt={productGroup.designName || productGroup.designCode}
                                style={{ width: 72, height: 72, objectFit: 'contain', borderRadius: 6, background: '#fff' }}
                              />
                            </Box>
                          )}
                          <Box>
                            <Typography variant="body1">
                              <strong>Design Code: </strong>
                              {productGroup.designCode}
                            </Typography>
                            <Typography variant="body2" style={{ marginTop: 4 }}>
                              <strong>Carton Capacity: </strong>
                              {productGroup.cartonCapacity || ''}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>

                {/* Customer detail (view only, similar to preview) */}
                <Box mt={2}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1">Customer Details</Typography>
                      {customer && (
                        <Box mt={1}>
                          <Typography variant="body1">
                            {customer.customerName || customer.name}
                          </Typography>
                          <Typography variant="body2" style={{ marginTop: 4 }}>
                            <strong>Mobile: </strong>
                            {customer.mobileNo || customer.phone || ''}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              </>
            );
          }}
        </FormDataConsumer>
      </SimpleForm>
    </Create>
  );
};
